import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, Chip, Tabs, Tab, Badge, Avatar, CardActionArea, Drawer, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { FaChartBar, FaDatabase, FaImages, FaBoxes, FaFileExport, FaTags, FaClock, FaFire } from 'react-icons/fa';
import { MdInventory, MdAnalytics, MdShoppingCart, MdDashboard, MdNew } from 'react-icons/md';
import { useFavorites } from '../../context/FavoritesContext';
import { usePermissions } from '../../context/PermissionContext';

const RECENT_VISITS_KEY = 'recentPageVisits';

const ExcelLanding = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { favorites, toggleFavorite, isFavorite } = useFavorites();
  const { isAdmin, isOwner, hasAccessToPath } = usePermissions();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedTool, setSelectedTool] = useState(null);
  const [lastUsedMap, setLastUsedMap] = useState({});

  // Load last used times from localStorage
  useEffect(() => {
    const loadLastUsedTimes = () => {
      try {
        const stored = localStorage.getItem(RECENT_VISITS_KEY);
        if (stored) {
          const visits = JSON.parse(stored);
          const timeMap = {};
          visits.forEach(visit => {
            if (!timeMap[visit.path]) {
              timeMap[visit.path] = new Date(visit.timestamp);
            }
          });
          setLastUsedMap(timeMap);
        }
      } catch (err) {
        console.error('Error loading last used times:', err);
      }
    };
    loadLastUsedTimes();
  }, []);

  // Format time ago from date
  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  };

  // Get last used time for a path
  const getLastUsed = (path) => {
    return formatTimeAgo(lastUsedMap[path]);
  };

  const getToolIcon = (name) => {
    if (name.toLowerCase().includes('general imports')) return MdDashboard;
    if (name.toLowerCase().includes('image')) return FaImages;
    if (name.toLowerCase().includes('inventory') || name.toLowerCase().includes('analysis')) return MdInventory;
    if (name.toLowerCase().includes('sales') || name.toLowerCase().includes('order')) return MdShoppingCart;
    if (name.toLowerCase().includes('export')) return FaFileExport;
    if (name.toLowerCase().includes('detail')) return FaDatabase;
    if (name.toLowerCase().includes('distribution')) return FaBoxes;
    if (name.toLowerCase().includes('product')) return FaTags;
    return FaChartBar;
  };

  const excelInterfaces = [
    {
      name: 'General Imports',
      path: '/dashboard/items-detail',
      description: 'Unified interface to export detailed item information by Design ID or Item ID for EU/US markets with comprehensive specifications, dimensions, and pricing.',
      category: 'Imports',
      isNew: false,
    },
    {
      name: 'Excel Export Item Images',
      path: '/dashboard/excel-export-item-images',
      description: 'Export item images with metadata in Excel format for catalog management.',
      category: 'Media',
      isNew: false,
    },
    // Inactive Items - DISABLED - Interface hidden
    // {
    //   name: 'Inactive Items',
    //   path: '/dashboard/inactive-items',
    //   description: 'Manage and analyze inactive inventory items for potential reactivation.',
    //   category: 'Inventory',
    //   isNew: false,
    // },
    // Item Image Grouping - DISABLED - Interface hidden
    // {
    //   name: 'Item Image Grouping',
    //   path: '/dashboard/item-image-grouping',
    //   description: 'Organize and group item images by categories or custom criteria.',
    //   category: 'Media',
    //   isNew: false,
    // },
    {
      name: 'PO Distribution',
      path: '/dashboard/po-distribution',
      description: 'Track and manage purchase order distributions across locations.',
      category: 'Orders',
      isNew: false,
    },
    {
      name: 'Canadian 13 Provinces Freight',
      path: '/dashboard/canadian-13-provinces-freight',
      description: 'Estimate Purolator Ground freight into Canada for a list of ItemIDs, using weights and package dimensions from Items Detail.',
      category: 'Orders',
      isNew: false,
    },
    {
      name: 'EU Freight',
      path: '/dashboard/eu-freight',
      description: 'Estimate UPS and FedEx parcel freight into Europe for a list of ItemIDs, side by side, using weights and package dimensions from the EU item file.',
      category: 'Orders',
      isNew: true,
    },
    {
      name: 'PO Distribution WHS',
      path: '/dashboard/po-distribution-whs',
      description: 'Distribute PO Units To Order across warehouses using historical sales-by-zip and current FUTQ levels (nearest-warehouse by lat/long).',
      category: 'Orders',
      isNew: false,
    },
    // Product Building - DISABLED
    // {
    //   name: 'Product Building',
    //   path: '/dashboard/product-building',
    //   description: 'Create and manage product configurations with components and variations.',
    //   category: 'Products',
    //   isNew: false,
    // },
    // SAF1909 Inventory Analysis - DISABLED - Interface hidden
    // {
    //   name: 'SAF1909 Inventory Analysis',
    //   path: '/dashboard/saf1909-inventory-analysis',
    //   description: 'Perform detailed inventory analysis for SAF1909 compliance.',
    //   category: 'Inventory',
    //   isNew: false,
    // },
    {
      name: 'Sales Orders Export',
      path: '/dashboard/sales-orders-export',
      description: 'Convert PDF sales orders to Excel format using multiple templates. Supports automated location processing and special order types.',
      category: 'Orders',
      isNew: false,
    },
    // Unpublished Items - DISABLED - Interface hidden
    // {
    //   name: 'Unpublished Items',
    //   path: '/dashboard/unpublished-items',
    //   description: 'Review and manage items that are not yet published to sales channels.',
    //   category: 'Products',
    //   isNew: false,
    // },
    // US Items Detail - DISABLED - Interface hidden
    // {
    //   name: 'US Items Detail',
    //   path: '/dashboard/us-items-detail',
    //   description: 'Access comprehensive product information for the US market.',
    //   category: 'Regional',
    //   isNew: false,
    // },
    {
      name: 'Excel Reports',
      path: '/dashboard/excel-reports',
      description: 'Download pre-generated Excel reports for various business metrics.',
      category: 'Reports',
      isNew: false,
    }
  ];

  const categories = ['All', 'Imports', 'Data Management', 'Media', 'Inventory', 'Orders', 'Products', 'Reports'];

  // Filter interfaces based on user permissions
  const permittedInterfaces = useMemo(() => {
    if (isAdmin || isOwner) {
      return excelInterfaces;
    }
    return excelInterfaces.filter(item => {
      // Extract path without /dashboard/ prefix for permission check
      // Handle special case for sales-order-creation which doesn't have /dashboard/ prefix
      const path = item.path.startsWith('/dashboard/')
        ? item.path.replace('/dashboard/', '')
        : item.path;
      return hasAccessToPath(path);
    });
  }, [isAdmin, isOwner, hasAccessToPath]);

  const filteredTools = selectedTab === 0
    ? permittedInterfaces
    : permittedInterfaces.filter(tool => tool.category === categories[selectedTab]);

  const handleToolClick = (item) => {
    setSelectedTool(item.path);
    navigate(item.path);
  };

  const sidebarContent = () => (
    <Paper
      elevation={0}
      sx={{
        width: 280,
        height: '100%',
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'white',
        borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Sidebar Header */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: '#228B22',
            }}
          >
            <RiFileExcel2Fill size={28} color="white" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Excel Tools
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {permittedInterfaces.length} tools available
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Category Tabs */}
      <Tabs
        orientation="vertical"
        value={selectedTab}
        onChange={(e, newValue) => { setSelectedTab(newValue); if (isMobile) setSidebarOpen(false); }}
        sx={{
          borderRight: 0,
          '& .MuiTabs-indicator': {
            left: 0,
            width: 4,
            bgcolor: '#228B22',
          },
        }}
      >
        {categories.map((category, index) => (
          <Tab
            key={category}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                <Typography variant="body2">{category}</Typography>
                {index === 0 && (
                  <Chip label={permittedInterfaces.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
                {index > 0 && (
                  <Chip
                    label={permittedInterfaces.filter(t => t.category === category).length}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            }
            sx={{
              textAlign: 'left',
              alignItems: 'flex-start',
              px: 3,
              py: 1.5,
              minHeight: 48,
              textTransform: 'none',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
              },
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(34, 139, 34, 0.08)' : 'rgba(34, 139, 34, 0.04)',
                transform: 'translateX(4px)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                width: '0%',
                height: '3px',
                borderRadius: '2px',
                transform: 'translateX(-50%)',
                transition: 'width 0.3s ease-in-out',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 10' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q25 0 50 5 T100 5' fill='none' stroke='%23228B22' stroke-width='4'/%3E%3C/svg%3E")`,
                backgroundSize: '100% 100%',
              },
              '&:hover::after': {
                width: '80%',
              },
            }}
          />
        ))}
      </Tabs>

      {/* Quick Stats */}
      <Box sx={{ mt: 'auto', p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Quick Stats
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Recently Used</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {permittedInterfaces.filter(t => lastUsedMap[t.path]).length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Favorites</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#FFD700' }}>
              {favorites.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">New Tools</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#FF6B6B' }}>
              {permittedInterfaces.filter(t => t.isNew).length}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: theme.palette.background.default }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)} PaperProps={{ sx: { width: 280 } }}>
          {sidebarContent()}
        </Drawer>
      ) : (
        sidebarContent()
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'white',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton onClick={() => setSidebarOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {categories[selectedTab]} Tools
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                Select a tool to begin working with your Excel data
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tools Grid */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(350px, 1fr))' }, gap: 2 }}>
            {filteredTools.map((item) => {
              const IconComponent = getToolIcon(item.name);
              const isFav = isFavorite(item.path);
              const isSelected = selectedTool === item.path;
              
              return (
                <Paper
                  key={item.path}
                  elevation={0}
                  sx={{
                    border: `2px solid ${isSelected ? '#228B22' : theme.palette.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: isSelected 
                      ? theme.palette.mode === 'dark' ? '#228B2210' : '#228B2205'
                      : 'transparent',
                    '&:hover': {
                      borderColor: '#228B22',
                      transform: 'translateX(4px)',
                      boxShadow: theme.shadows[2],
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: '0%',
                      height: '4px',
                      background: '#228B22',
                      borderRadius: '2px',
                      transform: 'translateX(-50%)',
                      transition: 'width 0.4s ease-in-out',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 10' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q25 0 50 5 T100 5' fill='none' stroke='%23228B22' stroke-width='4'/%3E%3C/svg%3E")`,
                      backgroundSize: '100% 100%',
                    },
                    '&:hover::after': {
                      width: '90%',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleToolClick(item)}
                    sx={{ p: 2.5, height: '100%' }}
                  >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Icon Section */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: '#228B2215',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent size={24} color="#228B22" />
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 600,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.name}
                          </Typography>
                          {item.isNew && (
                            <Chip
                              label="NEW"
                              size="small"
                              sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                height: 20,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                ml: 1,
                                flexShrink: 0
                              }}
                            />
                          )}
                        </Box>
                        <Tooltip title={isFav ? 'Remove from favorites' : 'Add to favorites'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite({ text: item.name, path: item.path, category: 'excel' });
                            }}
                            sx={{ 
                              ml: 1,
                              color: isFav ? '#FFD700' : theme.palette.action.disabled,
                            }}
                          >
                            {isFav ? '★' : '☆'}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.description}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaClock size={12} color={theme.palette.text.secondary} />
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {getLastUsed(item.path)}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip
                          label={item.category}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: '#228B2210',
                            color: '#228B22',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  </CardActionArea>
                </Paper>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ExcelLanding;